import { AxiosError } from 'axios';

/** Laravel validation and API payloads: prefer field errors over generic message. */
function extractDetailFromResponseData(data: unknown): string | null {
  if (typeof data === 'string' && data.trim() !== '') {
    return data;
  }
  if (!data || typeof data !== 'object') {
    return null;
  }
  const d = data as Record<string, unknown>;
  if (d.errors && typeof d.errors === 'object') {
    const messages = Object.values(d.errors as Record<string, unknown>)
      .flat()
      .filter((m): m is string => typeof m === 'string');
    if (messages.length > 0) {
      return messages.join(' ');
    }
  }
  if (typeof d.message === 'string' && d.message.trim() !== '') {
    return d.message;
  }
  if (typeof d.error === 'string') {
    return d.error;
  }
  if (d.error && typeof d.error === 'object' && 'message' in d.error) {
    const m = (d.error as { message?: unknown }).message;
    if (typeof m === 'string') {
      return m;
    }
  }
  return null;
}

/**
 * Extracts a user-friendly error message from an axios error
 */
export const getErrorMessage = (error: unknown): string => {
  // Handle null/undefined
  if (!error) {
    return 'An unexpected error occurred. Please try again.';
  }

  // Handle Redux Toolkit thunk errors - they serialize errors, so we need to check for serialized AxiosError
  if (error && typeof error === 'object') {
    const errorObj = error as any;
    
    // Helper function to extract status code from message if status is not directly available
    const extractStatusFromMessage = (message: string): number | null => {
      const statusMatch = message.match(/\b(\d{3})\b/);
      if (statusMatch) {
        const status = parseInt(statusMatch[1], 10);
        if (status >= 400 && status < 600) {
          return status;
        }
      }
      return null;
    };
    
    // Check if it's a serialized AxiosError (Redux Toolkit serializes errors)
    if (errorObj.name === 'AxiosError' || errorObj.isAxiosError || (errorObj.response && errorObj.config)) {
      // This is likely a serialized AxiosError — prefer API body (validation errors, etc.) over status text
      const fromData = extractDetailFromResponseData(errorObj.response?.data);
      if (fromData) {
        return fromData;
      }
      const status = errorObj.response?.status;
      if (status) {
        switch (status) {
          case 400:
            return 'Invalid request. Please check your input.';
          case 401:
            return 'You are not authorized. Please log in.';
          case 403:
            return 'You do not have permission to perform this action.';
          case 404:
            return 'The requested resource was not found.';
          case 422:
            return 'Validation error. Please check your input.';
          case 429:
            return 'Too many requests. Please wait a moment and try again.';
          case 500:
            return 'Server error. Please try again later.';
          case 503:
            return 'Service unavailable. Please try again later.';
        }
      }
      
      // PRIORITY 2: Check if status code is in the message
      if (errorObj.message && typeof errorObj.message === 'string') {
        const statusFromMessage = extractStatusFromMessage(errorObj.message);
        if (statusFromMessage === 429) {
          return 'Too many requests. Please wait a moment and try again.';
        }
        if (statusFromMessage) {
          // For other status codes found in message, use generic message
          return `Request failed with status ${statusFromMessage}. Please try again.`;
        }
      }
      
      // PRIORITY 3: Try to get message from response data
      if (errorObj.response?.data) {
        const data = errorObj.response.data;
        if (typeof data === 'string') {
          // Check if status code is in the data string
          const statusFromData = extractStatusFromMessage(data);
          if (statusFromData === 429) {
            return 'Too many requests. Please wait a moment and try again.';
          }
          return data;
        }
        if (data?.message) {
          const statusFromData = extractStatusFromMessage(data.message);
          if (statusFromData === 429) {
            return 'Too many requests. Please wait a moment and try again.';
          }
          return data.message;
        }
        if (data?.error) {
          const errorMsg = typeof data.error === 'string' ? data.error : data.error.message || 'An error occurred';
          const statusFromData = extractStatusFromMessage(errorMsg);
          if (statusFromData === 429) {
            return 'Too many requests. Please wait a moment and try again.';
          }
          return errorMsg;
        }
      }
      
      // PRIORITY 4: Return the error message if available (but check for status codes first)
      if (errorObj.message) {
        const statusFromMessage = extractStatusFromMessage(errorObj.message);
        if (statusFromMessage === 429) {
          return 'Too many requests. Please wait a moment and try again.';
        }
        return errorObj.message;
      }
    }
    
    // If it's a wrapped error, try to extract the original error
    if (errorObj.error) {
      const extracted = getErrorMessage(errorObj.error);
      if (extracted !== 'An unexpected error occurred. Please try again.') {
        return extracted;
      }
    }
    
    // Check if it has a message property - but check for status codes first
    if (errorObj.message && typeof errorObj.message === 'string') {
      const statusFromMessage = extractStatusFromMessage(errorObj.message);
      if (statusFromMessage === 429) {
        return 'Too many requests. Please wait a moment and try again.';
      }
      return errorObj.message;
    }
  }

  if (error instanceof Error) {
    // Check if it's an Axios error
    if ('isAxiosError' in error && (error as AxiosError).isAxiosError) {
      const axiosError = error as AxiosError<any>;
      
      // Try to get error message from response (field errors before generic Laravel message)
      if (axiosError.response?.data) {
        const detail = extractDetailFromResponseData(axiosError.response.data);
        if (detail) {
          return detail;
        }
      }

      // Handle HTTP status codes
      if (axiosError.response?.status) {
        switch (axiosError.response.status) {
          case 400:
            return 'Invalid request. Please check your input.';
          case 401:
            return 'You are not authorized. Please log in.';
          case 403:
            return 'You do not have permission to perform this action.';
          case 404:
            return 'The requested resource was not found.';
          case 422:
            return 'Validation error. Please check your input.';
          case 429:
            return 'Too many requests. Please wait a moment and try again.';
          case 500:
            return 'Server error. Please try again later.';
          case 503:
            return 'Service unavailable. Please try again later.';
          default:
            return axiosError.message || 'An error occurred';
        }
      }
      
      // Network error
      if (axiosError.code === 'ERR_NETWORK') {
        return 'Network error. Please check your connection.';
      }
      
      return axiosError.message || 'An error occurred';
    }
    
    // Regular Error object
    return error.message;
  }
  
  // Fallback for unknown error types
  return 'An unexpected error occurred. Please try again.';
};

